import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import {
  OfferDetailsFullScreenLayout,
  OfferDetailsLayout,
  OfferFullScreenLayout,
  useOfferDetailsLayout,
} from './offer-details.layout';
import { useOfferDetailsController } from './offer-details.controller.hook';
import { OfferPresentationType, PointsStrategy } from '../../graphql/types.generated';
import { OfferDetails } from '../../components/OfferDetails/OfferDetails';
import { ScreenLoaderBackdrop } from '../../components/ScreenLoader/ScreenLoader';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';
import {
  isOfferSweepstakesPoints,
  isOfferSweepstakesProduct,
  isSpinGameOffer,
} from '../../modules/offers/offer.type-guards';
import { useConfiguration } from '../../modules/configuration/useConfiguration';
import { PointsSweepsController } from '../Sweeps/PointsSweeps/PointsSweepsController';
import { useMembership } from '../../modules/membership/use-membership.hook';
import { ProductSweeps } from '../Sweeps/ProductSweeps';
import { useInfoActionSheet } from '../../components/modals/InfoActionSheet';
import { messages } from './offer-details.messages';
import { SpinToWinGameController } from '../Games/SpinToWin/SpinToWinGameController';

type OfferDetailsControllerProps = {
  returnPath: string;
};
export const OfferDetailsController = (props: OfferDetailsControllerProps) => {
  const { returnPath } = props;
  const { offerId = '' } = useParams<{ offerId: string }>();

  const intl = useIntl();
  const { triggerEvent } = useRudderStack();
  const { open: openInfoActionSheet } = useInfoActionSheet();
  const offerDetailsLayout = useOfferDetailsLayout({ returnPath });
  const pointsStrategy = useConfiguration('loyaltyProgram.pointsStrategy');
  const { membership } = useMembership();

  const { offer, isOfferLoading, isOfferVoucherLoading, action } = useOfferDetailsController({
    offerId,
    onOfferDetailsClose: offerDetailsLayout.handleClose,
  });

  useEffect(() => {
    if (offer?.id) {
      triggerEvent('offer_details_modal_opened', { uuid: offer.id });
    }
  }, [offer?.id, triggerEvent]);

  if (!offer && isOfferLoading) {
    return <ScreenLoaderBackdrop open />;
  }

  if (!offer) {
    return <Navigate to={returnPath} replace />;
  }

  if (isSpinGameOffer(offer)) {
    return (
      <OfferFullScreenLayout
        isOpen={offerDetailsLayout.isOpen}
        onClose={offerDetailsLayout.handleClose}
        onExited={offerDetailsLayout.handleExited}>
        <SpinToWinGameController
          sectors={offer.earningMechanism.awardRandomLoyalty.segments}
          termsAndConditionsLink={offer.marketingContent.termsUrl ?? ''}
          numberOfGamePlays={3}
        />
      </OfferFullScreenLayout>
    );
  }

  if (isOfferSweepstakesPoints(offer) && pointsStrategy === PointsStrategy.NotUsed) {
    return <Navigate to={returnPath} replace />;
  }

  if (isOfferSweepstakesPoints(offer)) {
    return (
      <OfferDetailsFullScreenLayout
        pointsStrategy={pointsStrategy}
        membershipPoints={membership.points}
        isOpen={offerDetailsLayout.isOpen}
        onClose={offerDetailsLayout.handleClose}
        onExited={offerDetailsLayout.handleExited}>
        <PointsSweepsController
          memberEntries={offer.earningMechanism.award.memberEntries}
          memberPoints={membership.points}
          offerId={offer.id}
          entryPrice={offer.earningMechanism.fulfilmentPrice}
          marketingContent={offer.marketingContent}
          onCompleted={(entriesAmount) => {
            offerDetailsLayout.handleClose();
            openInfoActionSheet({
              variant: 'congrats',
              title: intl.formatMessage(messages.buyEntriesCongratsDialogTitle, { entries: entriesAmount }),
              description: intl.formatMessage(messages.buyEntriesCongratsDialogDescription, { entries: entriesAmount }),
            });
          }}
        />
      </OfferDetailsFullScreenLayout>
    );
  }

  if (isOfferSweepstakesProduct(offer)) {
    return (
      <OfferDetailsFullScreenLayout
        pointsStrategy={pointsStrategy}
        membershipPoints={membership.points}
        isOpen={offerDetailsLayout.isOpen}
        onClose={offerDetailsLayout.handleClose}
        onExited={offerDetailsLayout.handleExited}>
        <ProductSweeps
          memberEntries={offer.earningMechanism.award.memberEntries}
          offerId={offer.id}
          marketingContent={offer.marketingContent}
          presentationType={offer.presentationType}
          // ProductSweeps offer can be activated, but for now UI does not support it
          // Slack Thread: https://stuzo.slack.com/archives/C04N6LF2WTV/p1698242120741569
          offerAction={null}
          onClose={offerDetailsLayout.handleClose}
        />
      </OfferDetailsFullScreenLayout>
    );
  }

  return (
    <OfferDetailsLayout
      isOpen={offerDetailsLayout.isOpen}
      onClose={offerDetailsLayout.handleClose}
      onExited={offerDetailsLayout.handleExited}>
      <OfferDetails
        isLoading={isOfferVoucherLoading}
        marketingContent={offer.marketingContent}
        action={action}
        variant={offer.presentationType === OfferPresentationType.Banner ? 'banner' : 'default'}
      />
    </OfferDetailsLayout>
  );
};
